import React, { useState, useEffect, useCallback } from "react";
import Loader from './Loader';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import Swal from 'sweetalert2';
import { formatDate } from '../utils/helper';
import './BookingDetailsSidebar.css';

const GOLD = '#c4941a';

const STATUS_ACTIONS = {
  pending: [
    { value: 'Confirmed', label: 'Confirm Booking', icon: 'bi-check-circle', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking',  icon: 'bi-x-circle',     cls: 'bd-btn-danger'  },
  ],
  confirmed: [
    { value: 'Completed', label: 'Mark Completed', icon: 'bi-patch-check', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking', icon: 'bi-x-circle',    cls: 'bd-btn-danger'  },
  ],
};

const statusPillClass = (status) => {
  const map = {
    pending:   'bd-status-pill bd-pending',
    confirmed: 'bd-status-pill bd-confirmed',
    completed: 'bd-status-pill bd-completed',
    cancelled: 'bd-status-pill bd-cancelled',
    refunded:  'bd-status-pill bd-refunded',
    active:    'bd-status-pill bd-confirmed',
  };
  return map[status?.toLowerCase()] || 'bd-status-pill bd-default';
};

const BookingDetailsSidebar = ({ bookingId, selectedBooking, onUpdate }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [booking, setBooking] = useState(null);

  const getBookingData = useCallback(async () => {
    if (!bookingId) return;
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/bookings/${bookingId}` });
      if (res.data.status) setBooking(res.data.data);
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to load booking details');
    } finally {
      setIsLoader(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) getBookingData();
    else if (selectedBooking) setBooking(selectedBooking);
  }, [bookingId, selectedBooking, getBookingData]);

  const handleStatusUpdate = async (newStatus) => {
    const b = booking;
    if (!b?._id) { toast.error('Booking not found'); return; }

    const result = await Swal.fire({
      title: `Mark as ${newStatus}`,
      text: `Update this booking status to ${newStatus}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: GOLD,
      confirmButtonText: `Yes, ${newStatus}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'POST',
        url: `admin/bookings/${b._id}/status`,
        data: { status: newStatus, payment_status: b.payment_status, notes: b.notes || '' },
      });
      if (res.data.status) {
        toast.success(`Booking marked as ${newStatus}`);
        try {
          const full = await ApiService.request({ method: 'GET', url: `admin/bookings/${b._id}` });
          const updated = full.data.status ? full.data.data : res.data.data;
          setBooking(updated);
          onUpdate?.(updated);
        } catch {
          setBooking(res.data.data);
          onUpdate?.(res.data.data);
        }
      } else {
        toast.error(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update booking status');
      console.error(err);
    } finally {
      setIsLoader(false);
    }
  };

  const fmt = (amount) => `AED ${Number(amount ?? 0).toLocaleString()}`;

  const b = booking;
  const currentActions = STATUS_ACTIONS[b?.status?.toLowerCase()] || [];

  if (isLoader && !b) {
    return (
      <div className="d-flex justify-content-center align-items-center py-10">
        <Loader text="Loading..." size={35} />
      </div>
    );
  }

  if (!b) return null;

  const bookingCode = b.booking_code || `#${b._id?.toString().slice(-8)}`;
  const services = b.services || b.service_ids || [];
  const serviceName = services[0]?.title || services[0]?.name || '—';
  const venue = b.address_id?.complete_address || b.partner_id?.address || null;
  const eventDateTime = b.event_date
    ? `${formatDate(b.event_date, 'MMM DD, YYYY')}${b.event_time ? ` at ${b.event_time}` : ''}`
    : 'N/A';
  const paidAmount = b.paid_amount ??
    (String(b.payment_status).toLowerCase() === 'paid' ? (b.grand_total ?? b.total_amount) : 0);

  const copyCode = () => {
    navigator.clipboard?.writeText(bookingCode);
    toast.success('Booking ID copied', { autoClose: 1500 });
  };

  return (
    <div className="booking-sidebar-wrapper">
      {isLoader && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader text="Processing" size={40} />
        </div>
      )}

      {/* Status + code */}
      <div className="bd-top-row">
        <span className={`${statusPillClass(b.status)} text-uppercase`}>{b.status || 'N/A'}</span>
        <button className="bd-code-btn" onClick={copyCode} title="Copy booking ID">
          {bookingCode}
          <i className="bi bi-clipboard"></i>
        </button>
      </div>

      {/* Customer */}
      <div className="bd-customer">
        <div className="bd-customer-avatar">
          {b.user_id?.picture || b.user_id?.image ? (
            <img
              src={b.user_id.picture || b.user_id.image}
              alt={b.user_id?.name}
              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(b.user_id?.name || 'U')}&background=F1E9DA&color=B98A4B&size=128`; }}
            />
          ) : (
            <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(b.user_id?.name || 'U')}&background=F1E9DA&color=B98A4B&size=128`} alt={b.user_id?.name} />
          )}
        </div>
        <div className="bd-customer-info">
          <div className="bd-customer-name">{b.user_id?.name || 'N/A'}</div>
          <div className="bd-customer-line"><i className="bi bi-telephone"></i>{b.user_id?.phone || 'N/A'}</div>
          <div className="bd-customer-line"><i className="bi bi-envelope"></i>{b.user_id?.email || 'N/A'}</div>
        </div>
      </div>

      {/* Booking Information */}
      <div className="bd-section">
        <h6 className="bd-section-title">Booking Information</h6>
        <div className="bd-row">
          <span className="bd-label">Service</span>
          <span className="bd-value">{serviceName}</span>
        </div>
        {services.length > 1 && (
          <div className="bd-row">
            <span className="bd-label">Other Services</span>
            <span className="bd-value">{services.slice(1).map((s) => s.title || s.name).join(', ')}</span>
          </div>
        )}
        <div className="bd-row">
          <span className="bd-label">Provider</span>
          <span className="bd-value">{b.partner_id?.company_name || 'N/A'}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Event Date &amp; Time</span>
          <span className="bd-value">{eventDateTime}</span>
        </div>
        {b.event_type && (
          <div className="bd-row">
            <span className="bd-label">Event Type</span>
            <span className="bd-value text-capitalize">{b.event_type}</span>
          </div>
        )}
        {venue && (
          <div className="bd-row">
            <span className="bd-label">Venue</span>
            <span className="bd-value">{venue}</span>
          </div>
        )}
        {(b.guests || b.no_of_guests) && (
          <div className="bd-row">
            <span className="bd-label">Guests</span>
            <span className="bd-value">{b.guests || b.no_of_guests} Guests</span>
          </div>
        )}
      </div>

      {/* Payment Summary */}
      <div className="bd-section">
        <h6 className="bd-section-title">Payment Summary</h6>
        <div className="bd-row">
          <span className="bd-label">Subtotal</span>
          <span className="bd-value">{fmt(b.total_price)}</span>
        </div>
        {b.service_charges > 0 && (
          <div className="bd-row">
            <span className="bd-label">Service Charges</span>
            <span className="bd-value">{fmt(b.service_charges)}</span>
          </div>
        )}
        {/* <div className="bd-row">
          <span className="bd-label">
            Discount{b.promo_code && <span className="bd-tag">{b.promo_code}</span>}
          </span>
          <span className="bd-value">− {fmt(b.promo_discount)}</span>
        </div> */}
        <div className="bd-row bd-total-row">
          <span className="bd-label bd-strong">Total Amount</span>
          <span className="bd-value bd-strong">{fmt(b.grand_total ?? b.total_amount)}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Paid Amount</span>
          <span className="bd-value">{fmt(paidAmount)}</span>
        </div>
        {b.commission_amount > 0 && (
          <div className="bd-row">
            <span className="bd-label">
              Commission{b.commission_rate > 0 && <span className="bd-muted"> ({b.commission_rate}%)</span>}
            </span>
            <span className="bd-value bd-danger">− {fmt(b.commission_amount)}</span>
          </div>
        )}
        {(b.partner_earning || b.commission_amount > 0) && (
          <div className="bd-row">
            <span className="bd-label">Partner Earning</span>
            <span className="bd-value bd-success">
              {fmt(b.partner_earning ?? ((b.total_price || 0) - (b.commission_amount || 0)))}
            </span>
          </div>
        )}
        <div className="bd-row">
          <span className="bd-label">Payment Method</span>
          <span className="bd-value text-capitalize">{b.payment_method?.replace(/_/g, ' ') || 'N/A'}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Payment Status</span>
          <span className={`bd-pay-pill ${String(b.payment_status).toLowerCase() === 'paid' ? 'bd-paid' : 'bd-unpaid'}`}>
            {b.payment_status || 'N/A'}
          </span>
        </div>
      </div>

      {/* Actions */}
      {currentActions.length > 0 && (
        <div className="bd-actions">
          <h6 className="bd-section-title">Actions</h6>
          {currentActions.map(({ value, label, icon, cls }) => (
            <button
              key={value}
              className={`bd-action-btn ${cls}`}
              disabled={isLoader}
              onClick={() => handleStatusUpdate(value)}
            >
              <i className={`bi ${icon} me-2`}></i>{label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingDetailsSidebar;
